import { createContext, useContext } from "react";
import { useT, type Locale } from "@/i18n";

// The ONE place dates are written for people. The browser's own locale is not the app's: a French
// screen on an English-set Chrome printed "10/27/2026, 1:58:34 PM" (seen 2026-10-27).
const TAG: Record<Locale, string> = { fr: "fr-CH", en: "en-GB", de: "de-CH", it: "it-CH" };

// A warehouse's own clock, the one its Slack messages are written in (JobRecap.zone_for): Hackbridge
// (34) in London, Berlin (4) in Berlin, every other warehouse in Zurich.
export function zoneOf(warehouseId: number | null | undefined) {
  return warehouseId === 34 ? "Europe/London" : warehouseId === 4 ? "Europe/Berlin" : "Europe/Zurich";
}
// The open job's warehouse clock, for everything drawn inside it (issues, the summary, the reset, photo
// captions); Zurich outside a job.
export const WarehouseZone = createContext("Europe/Zurich");

// "27.10.26" — the short day his screens use, the same in every language.
export function shortDay(iso: string | null | undefined) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y.slice(2)}`;
}

export function useFormat() {
  const { locale } = useT();
  const tag = TAG[locale];
  const zone = useContext(WarehouseZone);
  return {
    shortDay,
    // His `loc()` stamp on the job's warehouse clock: an issue, the summary, a reset, a photo caption.
    stamp: (iso: string | null | undefined) => stampAt(iso, locale, zone),
    // His `dayLbl`: "Mon 03.11" — the short weekday in the reader's language, then the day.
    dayLbl: (iso: string | null | undefined) => {
      if (!iso) return "";
      const wd = new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(tag, { weekday: "short" }).replace(".", "");
      return `${wd} ${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
    },
    time: (iso: string | null | undefined) =>
      iso ? new Date(iso).toLocaleTimeString(tag, { hour: "2-digit", minute: "2-digit" }) : "",
    dateTime: (iso: string | null | undefined) =>
      iso ? new Date(iso).toLocaleString(tag, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "",
    // A time that belongs to the warehouses' clock — the schedule is set in Zurich time, so its runs
    // are written in Zurich time too, wherever the reader is (15:00 must not read 19:30 from India).
    // His sync line's two halves, "28.10" and "14:05", on the warehouses' clock.
    zurichDayMonth: (ms: number) => new Date(ms).toLocaleDateString(tag, { day: "2-digit", month: "2-digit", timeZone: "Europe/Zurich" }),
    zurichTime: (ms: number) => new Date(ms).toLocaleTimeString(tag, { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" }),
    zurichDateTime: (iso: string | null | undefined) =>
      iso ? new Date(iso).toLocaleString(tag, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" }) : "",
  };
}

// His `loc()` stamp — "30/10, 17:29" in English, "30.10 17:29" in every other language (fr-CH), no
// year — in the given zone.
export function stampAt(iso: string | null | undefined, locale: Locale, zone: string) {
  return iso ? new Date(iso).toLocaleString(locale === "en" ? "en-GB" : "fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: zone }) : "";
}

// Today on this device's own clock, as yyyy-mm-dd — his `today()` (`isoD(new Date())`), which his
// "to do · overdue" pill compares the assembly day with.
export function todayHere() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Today in the warehouse's own zone, as yyyy-mm-dd — a day boundary must not move with the viewer.
export function todayInZurich() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

// His `fmin`: rounded to the minute, never below 0 — "2 h 05", "2 h", "45 min".
export function fmin(minutes: number) {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60), r = m % 60;
  return h && r ? `${h} h ${String(r).padStart(2, "0")}` : h ? `${h} h` : `${r} min`;
}
